import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdArrayUtil, OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type { FilterConditionPlugin } from '../filter';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

/**
 * Condition id of {@link ClinicsFilterIdsCondition} — deliberately singular,
 * while the plugin TYPE is `clinicsFilterIds`. The two are matched separately:
 * registrations resolve a stored tree by condition id, the offered-plugin
 * allow-lists by plugin type.
 */
export const AYS_CLINIC_FILTER_ID_CONDITION_ID = 'clinicFilterId';

export const FilterConditionClinicsFilterIdsPluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionClinicsFilterIdsDto,
  ClinicsFilterIdsCondition
> = {
  type: 'clinicsFilterIds',
  category: 'clinic',
  displayName: { de: 'Klinik-Filter' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionClinicsFilterIdsDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionClinicsFilterIdsPlugin(obj.includes ?? []);
  },
  fromCondition(
    jsonObj: ClinicsFilterIdsCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionClinicsFilterIdsPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionClinicsFilterIdsPlugin([]);
  },
};

export class FilterConditionClinicsFilterIdsPlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionClinicsFilterIdsDto
> {
  static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionClinicsFilterIdsDto,
    ClinicsFilterIdsCondition
  > = FilterConditionClinicsFilterIdsPluginType;

  constructor(private includes: string[]) {
    super(FilterConditionClinicsFilterIdsPluginType.type);
  }
  override clone(): FilterConditionClinicsFilterIdsPlugin {
    return new FilterConditionClinicsFilterIdsPlugin([...this.includes]);
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionPlugin<string[]> {
    return new FilterConditionClinicsFilterIdsPlugin(
      this.includes
        ? OverridePropertyHelper.overrideProperties(
            this.includes,
            'clinicsFilterId',
            override,
            (value) => value,
          )
        : [],
    );
  }

  override equals(other: FilterConditionClinicsFilterIdsPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.includes, other.includes)
    );
  }

  override getOverrideProperties(tileId: string): OverrideProperty[] {
    return this.includes.map(
      (clinicFilterId) =>
        new OverrideProperty(
          tileId,
          'clinicsFilterId',
          clinicFilterId,
          clinicFilterId,
          'possible',
        ),
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    return { [ShareableType.CLINICS_FILTER]: this.includes };
  }

  override validationToText(): string {
    if (this.includes.length === 0) {
      return 'Mindestens einen Klinik-Filter auswählen';
    } else {
      return '';
    }
  }

  override toCondition(): BdCondition {
    return new ClinicsFilterIdsCondition(this.includes, undefined);
  }

  override toJSON(): FilterConditionClinicsFilterIdsDto {
    return {
      type: this.type,
      includes: this.includes,
    };
  }

  override toValue(): string[] {
    return this.includes;
  }

  override toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.includes.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    return { title, items };
  }

  override toText(): string {
    return this.includes.length === 1 ? 'ein Klinik-Filter' : `${this.includes.length} Klinik-Filter`;
  }
}

export class ClinicsFilterIdsCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(AYS_CLINIC_FILTER_ID_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionClinicsFilterIdsDto {
  type: string;
  includes: string[];
}
